import { useState } from "react";
import { Link } from "react-router-dom";
import { useCustomers, useUpsertCustomer, type CustomerType } from "@/lib/db";

export default function KundenPage() {
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const { data: customers = [], isLoading } = useCustomers(search);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Kunden</h1>
        <button onClick={() => setNewOpen(true)} className="btn-primary">+ Neuer Kunde</button>
      </div>

      <div className="card">
        <input
          className="input"
          placeholder="Suche nach Name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="card">Lade…</div>
      ) : customers.length === 0 ? (
        <div className="card text-center text-hof-600">
          {search ? "Keine Treffer." : "Noch keine Kunden — leg den ersten an."}
        </div>
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-hof-50 text-sm text-hof-600">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Typ</th>
                <th className="p-3">Telefon</th>
                <th className="p-3">E-Mail</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t border-hof-100 hover:bg-hof-50/50">
                  <td className="p-3">
                    <Link to={`/backoffice/kunden/${c.id}`} className="font-medium text-wiese-700 hover:underline">
                      {c.display_name}
                    </Link>
                  </td>
                  <td className="p-3">
                    {c.type === "gewerblich" ? <span className="pill-info">Gewerblich</span> : <span className="pill bg-hof-100 text-hof-800">Privat</span>}
                  </td>
                  <td className="p-3 text-hof-700">{c.phone ?? "—"}</td>
                  <td className="p-3 text-hof-700">{c.email ?? "—"}</td>
                  <td className="p-3">
                    {c.is_active ? <span className="pill-success">aktiv</span> : <span className="pill-warn">inaktiv</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && <NeuerKundeDrawer onClose={() => setNewOpen(false)} />}
    </div>
  );
}

function NeuerKundeDrawer({ onClose }: { onClose: () => void }) {
  const upsert = useUpsertCustomer();
  const [display_name, setName] = useState("");
  const [type, setType] = useState<CustomerType>("privat");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await upsert.mutateAsync({
        display_name: display_name.trim(),
        type,
        phone: phone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
        is_active: true,
      });
      onClose();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 flex justify-end" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}
        className="bg-hof-50 w-full max-w-md h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-hof-900">Neuer Kunde</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-2">✕</button>
        </div>

        <div>
          <label className="label">Name *</label>
          <input className="input" value={display_name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>

        <div>
          <label className="label">Typ</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value as CustomerType)}>
            <option value="privat">Privatkunde</option>
            <option value="gewerblich">Gewerblich</option>
          </select>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="label">Telefon</label>
            <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 ..." />
          </div>
          <div>
            <label className="label">E-Mail</label>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="label">Interne Notizen</label>
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="z.B. Hundenotiz, Wunschzeit, Allergien …" />
        </div>

        {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}

        <button type="submit" className="btn-primary w-full" disabled={upsert.isPending}>
          {upsert.isPending ? "Anlegen…" : "Anlegen"}
        </button>
        <p className="text-xs text-hof-500 text-center">
          Adressen + SEPA + Abos werden auf der Detail-Seite gepflegt.
        </p>
      </form>
    </div>
  );
}
